import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    IconButton,
    useMediaQuery,
    useTheme,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    CircularProgress,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { theme as appTheme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { updateGlobalConfiguration, getGlobalConfig } from '../../redux/configuration/action';
import { RootState } from '../../redux/root-reducer';
import { IGlobalConfigurationUpdate } from '../../redux/configuration/types';
import { countries } from '../../utils/countriesList';
import { enrichGlobalConfigForEditor, GlobalConfigurationUgcEditor } from './globalConfigurationUgcEditor';
import { GlobalConfigurationBuzzbridgeLoyaltyEditor } from './globalConfigurationBuzzbridgeLoyaltyEditor';
import { GlobalConfigurationContentScrubEditor } from './GlobalConfigurationContentScrubEditor';
import { GlobalConfigurationCampaignHostLtdEditor } from './GlobalConfigurationCampaignHostLtdEditor';
import { buildGlobalConfigurationPatch, buildGlobalConfigurationSaveSteps } from '../../utils/globalConfigurationPatch';

interface GlobalConfigurationModalProps {
    open: boolean;
    onClose: () => void;
}

/** Editable USD field backed by cents — avoids re-formatting on every keystroke (which blocks editing from $0.00). */
function GlobalConfigurationDollarCentsField({
    label,
    cents,
    onChangeCents,
    description,
    themeStyle,
    placeholder = '60.00',
}: {
    label: string;
    cents?: number;
    onChangeCents: (cents: number) => void;
    description?: string;
    themeStyle: keyof typeof appTheme;
    placeholder?: string;
}) {
    const [input, setInput] = useState('');
    const isEditingRef = useRef(false);

    const formatCentsInput = (value?: number) =>
        value != null && Number.isFinite(value) && value > 0 ? (value / 100).toFixed(2) : '';

    useEffect(() => {
        if (isEditingRef.current) return;
        setInput(formatCentsInput(cents));
    }, [cents]);

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            color: appTheme[themeStyle].tag_white,
            '& fieldset': { borderColor: appTheme[themeStyle].form_divider },
            '&:hover fieldset': { borderColor: appTheme[themeStyle].highlight },
        },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                    sx={{
                        color: appTheme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        minWidth: 200,
                    }}>
                    {label}
                </Typography>
                <TextField
                    value={input}
                    onFocus={() => {
                        isEditingRef.current = true;
                    }}
                    onChange={e => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                        setInput(raw);
                        if (raw === '' || raw === '.') return;
                        const num = parseFloat(raw);
                        if (Number.isFinite(num)) {
                            onChangeCents(Math.round(num * 100));
                        }
                    }}
                    onBlur={() => {
                        isEditingRef.current = false;
                        if (input === '' || input === '.') return;
                        const num = parseFloat(input);
                        if (!Number.isFinite(num)) return;
                        const normalized = Math.round(num * 100);
                        onChangeCents(normalized);
                        setInput((normalized / 100).toFixed(2));
                    }}
                    placeholder={placeholder}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Typography sx={{ color: appTheme[themeStyle].grey, fontSize: 14 }}>$</Typography>
                            </InputAdornment>
                        ),
                    }}
                    sx={fieldSx}
                />
            </Box>
            {description && (
                <Typography
                    sx={{
                        color: appTheme[themeStyle].grey,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        maxWidth: '600px',
                    }}>
                    {description}
                </Typography>
            )}
        </Box>
    );
}

const GlobalConfigurationModal: React.FC<GlobalConfigurationModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const globalConfiguration = useSelector((state: RootState) => state.configuration.globalConfiguration);
    const updatingConfig = useSelector((state: RootState) => state.configuration.updatingConfig);
    const fetchingGlobalConfig = useSelector((state: RootState) => state.configuration.fetchingGlobalConfig);

    const [config, setConfig] = useState<IGlobalConfigurationUpdate>({});
    const [baselineConfig, setBaselineConfig] = useState<IGlobalConfigurationUpdate>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<string>('NG'); // Default to Nigeria
    const [applyToAllCountries, setApplyToAllCountries] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
    const lastSaveApplyToAllRef = useRef(false);

    useEffect(() => {
        if (!open) return;
        dispatch(getGlobalConfig(selectedCountry));
    }, [open, selectedCountry, dispatch]);

    // Sync from server when not mid-edit (avoids wiping in-progress changes on refetch)
    useEffect(() => {
        if (!globalConfiguration || hasChanges || isSaving) return;

        const deepCopy = enrichGlobalConfigForEditor(JSON.parse(JSON.stringify(globalConfiguration)));
        setConfig(deepCopy);
        setBaselineConfig(deepCopy);

        const playlistValue = deepCopy.past_winner_playlist;
        if (playlistValue !== undefined) {
            const isValid = !playlistValue || playlistValue.startsWith('PL-');
            setValidationErrors(prev => ({
                ...prev,
                past_winner_playlist: !isValid,
            }));
        }
    }, [globalConfiguration, hasChanges, isSaving]);

    useEffect(() => {
        if (open && globalConfiguration) {
            setHasChanges(false);
            setIsSaving(false);
            setApplyToAllCountries(false);
            setValidationErrors({});
        }
    }, [open, globalConfiguration]);

    // Close modal when per-country update succeeds; keep open after apply-to-all so fields refresh in view
    useEffect(() => {
        if (!updatingConfig && isSaving && hasChanges && globalConfiguration) {
            const stayOpen = lastSaveApplyToAllRef.current;
            setHasChanges(false);
            setIsSaving(false);
            setApplyToAllCountries(false);
            lastSaveApplyToAllRef.current = false;
            if (!stayOpen) {
                onClose();
            }
        }
    }, [updatingConfig, globalConfiguration, onClose, isSaving, hasChanges]);

    // Reset config when modal closes
    useEffect(() => {
        if (!open) {
            setHasChanges(false);
            setIsSaving(false);
            setApplyToAllCountries(false);
            setValidationErrors({});
            if (globalConfiguration) {
                const deepCopy = enrichGlobalConfigForEditor(JSON.parse(JSON.stringify(globalConfiguration)));
                setConfig(deepCopy);
                setBaselineConfig(deepCopy);
            }
        }
    }, [open, globalConfiguration]);

    // Check if there are any changes by comparing config with loaded baseline
    useEffect(() => {
        if (Object.keys(baselineConfig).length > 0 && Object.keys(config).length > 0) {
            const patch = buildGlobalConfigurationPatch(config, baselineConfig);
            const hasAnyChanges = Object.keys(patch).length > 0;
            if (hasAnyChanges !== hasChanges) {
                setHasChanges(hasAnyChanges);
            }
        }
    }, [config, baselineConfig, hasChanges]);

    const pendingPatch = useMemo(() => buildGlobalConfigurationPatch(config, baselineConfig), [config, baselineConfig]);
    const pendingPatchFieldLabels = useMemo(
        () => Object.keys(pendingPatch).map(key => key.replace(/_/g, ' ')),
        [pendingPatch],
    );

    // Show loading state if no configuration is available
    if (!globalConfiguration && !fetchingGlobalConfig) {
        return null;
    }

    // Show loading state while fetching
    if (fetchingGlobalConfig) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={isMobile}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: appTheme[themeStyle].deep_grey_lighter,
                        backgroundImage: 'none',
                        minHeight: isMobile ? '100vh' : 600,
                        maxHeight: isMobile ? '100vh' : '90vh',
                        borderRadius: isMobile ? 0 : 2,
                    },
                }}>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: appTheme[themeStyle].highlight }} />
                </DialogContent>
            </Dialog>
        );
    }

    // Ensure config is properly initialized
    if (!config || Object.keys(config).length === 0) {
        return null;
    }

    // Helper function to safely get nested object values
    const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    };

    // Helper function to safely set nested object values
    const setNestedValue = (obj: any, path: string, value: any) => {
        const keys = path.split('.');
        let current = obj;

        // Navigate to the parent object, creating missing objects as we go
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
                current[keys[i]] = {};
            }
            current = current[keys[i]];
        }

        // Set the value at the final key
        current[keys[keys.length - 1]] = value;
    };

    const handleToggle = (path: string, value?: 'active' | 'inactive' | boolean | number) => {
        const newConfig = JSON.parse(JSON.stringify(config));

        // Get the current value from the current config (not the original)
        const currentValue = getNestedValue(newConfig, path);

        // If a specific value is provided, use it
        if (value !== undefined) {
            setNestedValue(newConfig, path, value);
        } else {
            // For toggle switches, switch between active/inactive or true/false
            let newValue;
            if (typeof currentValue === 'boolean') {
                newValue = !currentValue;
            } else if (currentValue === 'active' || currentValue === 'inactive') {
                newValue = currentValue === 'active' ? 'inactive' : 'active';
            } else {
                // Default to active if no current value
                newValue = 'active';
            }
            setNestedValue(newConfig, path, newValue);
        }

        setConfig(newConfig);
        setHasChanges(true);
    };

    // Helper function to convert bytes to MB for display
    const formatBytesToMB = (bytes: number) => {
        return (bytes / (1024 * 1024)).toFixed(2);
    };

    // Helper function to format bytes to readable format (B, KB, MB, GB)
    const formatBytesReadable = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Helper function to convert MB to bytes for saving
    const parseMBToBytes = (mb: string) => {
        const num = parseFloat(mb);
        return isNaN(num) ? 0 : Math.round(num * 1024 * 1024);
    };

    // Helper function to handle number changes for non-byte fields
    const handleNumberChange = (path: string, value: string) => {
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
            handleToggle(path, numValue);
        }
    };

    // Helper function to handle string changes
    const handleStringChange = (path: string, value: string, validation?: (value: string) => boolean) => {
        const newConfig = JSON.parse(JSON.stringify(config));
        setNestedValue(newConfig, path, value);
        setConfig(newConfig);
        setHasChanges(true);

        // Update validation errors
        if (validation) {
            const isValid = validation(value);
            setValidationErrors(prev => ({
                ...prev,
                [path]: !isValid,
            }));
        }
    };

    const formatPrice = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const parsePrice = (dollars: string) => {
        const num = parseFloat(dollars.replace('$', ''));
        return isNaN(num) ? 0 : Math.round(num * 100);
    };

    const handleCountryChange = (countryCode: string) => {
        setSelectedCountry(countryCode);
        setConfig({});
        setBaselineConfig({});
        setHasChanges(false);
        setIsSaving(false);
        setApplyToAllCountries(false);
        setValidationErrors({});
        dispatch(getGlobalConfig(countryCode));
    };

    const handleSave = () => {
        if (!hasChanges) return;

        // Check if there are any validation errors
        const hasValidationErrors = Object.values(validationErrors).some(error => error === true);
        if (hasValidationErrors) {
            return;
        }

        const patch = buildGlobalConfigurationPatch(config, baselineConfig);
        const steps = buildGlobalConfigurationSaveSteps(patch, applyToAllCountries);
        if (steps.length === 0) return;

        lastSaveApplyToAllRef.current = applyToAllCountries;
        setIsSaving(true);
        dispatch(updateGlobalConfiguration({ country: selectedCountry, steps }));
    };

    // Check if save button should be disabled
    const isSaveDisabled =
        !hasChanges ||
        updatingConfig ||
        fetchingGlobalConfig ||
        isSaving ||
        Object.values(validationErrors).some(error => error === true);

    const renderToggleSection = (title: string, items: Array<{ key: string; label: string; path: string }>) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    {items.map(({ key, label, path }) => {
                        const currentValue = getNestedValue(config, path);
                        return (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Switch
                                        checked={currentValue === 'active' || currentValue === true}
                                        onChange={() => handleToggle(path)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: appTheme[themeStyle].highlight,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: appTheme[themeStyle].highlight,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {label}
                                    </Typography>
                                }
                            />
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    const renderNumberSectionWithDescription = (
        title: string,
        items: Array<{
            key: string;
            label: string;
            path: string;
            description?: string;
            isPrice?: boolean;
            isBytes?: boolean;
            isSlider?: boolean;
        }>,
    ) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={3}>
                    {items.map(({ key, label, path, description, isPrice, isBytes, isSlider }) => {
                        const currentValue = getNestedValue(config, path);
                        let displayValue: string;

                        if (isPrice) {
                            displayValue = formatPrice(currentValue || 0);
                        } else if (isBytes) {
                            displayValue = formatBytesToMB(currentValue || 0);
                        } else {
                            displayValue = (currentValue || 0).toString();
                        }

                        return (
                            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            minWidth: 200,
                                        }}>
                                        {label}
                                    </Typography>
                                    <TextField
                                        value={displayValue}
                                        onChange={e => {
                                            if (isPrice) {
                                                const cents = parsePrice(e.target.value);
                                                handleToggle(path, cents);
                                            } else if (isBytes) {
                                                const bytes = parseMBToBytes(e.target.value);
                                                handleToggle(path, bytes);
                                            } else {
                                                handleNumberChange(path, e.target.value);
                                            }
                                        }}
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: appTheme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: appTheme[themeStyle].form_divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: appTheme[themeStyle].highlight,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: appTheme[themeStyle].grey,
                                            },
                                        }}
                                    />
                                    {isBytes && (
                                        <Typography
                                            sx={{
                                                color: appTheme[themeStyle].grey,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                            }}>
                                            MB
                                        </Typography>
                                    )}
                                </Box>
                                {description && (
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].grey,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.75rem',
                                            fontStyle: 'italic',
                                            ml: 0,
                                            maxWidth: '600px',
                                        }}>
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    const renderStringSection = (
        title: string,
        items: Array<{
            key: string;
            label: string;
            path: string;
            description?: string;
            validation?: (value: string) => boolean;
            errorMessage?: string;
            placeholder?: string;
        }>,
    ) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={3}>
                    {items.map(({ key, label, path, description, validation, errorMessage, placeholder }) => {
                        const currentValue = getNestedValue(config, path) || '';
                        const isValid = validation ? validation(currentValue) : true;

                        return (
                            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            minWidth: 200,
                                        }}>
                                        {label}
                                    </Typography>
                                    <TextField
                                        value={currentValue}
                                        onChange={e => handleStringChange(path, e.target.value, validation)}
                                        placeholder={placeholder}
                                        error={!isValid}
                                        helperText={!isValid ? errorMessage : ''}
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: appTheme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: appTheme[themeStyle].form_divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: appTheme[themeStyle].highlight,
                                                },
                                                '&.Mui-error fieldset': {
                                                    borderColor: appTheme[themeStyle].red_status_indicator || '#f44336',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: appTheme[themeStyle].grey,
                                            },
                                            '& .MuiFormHelperText-root': {
                                                color: appTheme[themeStyle].red_status_indicator || '#f44336',
                                            },
                                        }}
                                    />
                                </Box>
                                {description && (
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].grey,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.75rem',
                                            fontStyle: 'italic',
                                            ml: 0,
                                            maxWidth: '600px',
                                        }}>
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    const renderNumberSection = (
        title: string,
        items: Array<{
            key: string;
            label: string;
            path: string;
            isPrice?: boolean;
            isBytes?: boolean;
            isSlider?: boolean;
        }>,
    ) => (
        <Accordion
            defaultExpanded
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': {
                    color: appTheme[themeStyle].tag_white,
                },
                '& .MuiAccordionDetails-root': {
                    color: appTheme[themeStyle].tag_white,
                },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    {items.map(({ key, label, path, isPrice, isBytes, isSlider }) => {
                        const currentValue = getNestedValue(config, path);
                        let displayValue: string;

                        if (isPrice) {
                            displayValue = formatPrice(currentValue || 0);
                        } else if (isBytes) {
                            displayValue = formatBytesToMB(currentValue || 0);
                        } else {
                            displayValue = (currentValue || 0).toString();
                        }

                        if (isSlider) {
                            // Render slider for file size fields
                            return (
                                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {label}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Slider
                                            value={currentValue || 0}
                                            onChange={(_, value) => handleToggle(path, value as number)}
                                            min={0}
                                            max={10 * 1024 * 1024 * 1024} // 10GB in bytes
                                            step={1024 * 1024} // 1MB steps
                                            sx={{
                                                flex: 1,
                                                '& .MuiSlider-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSlider-rail': {
                                                    backgroundColor: appTheme[themeStyle].form_divider,
                                                },
                                                '& .MuiSlider-thumb': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                    '&:hover': {
                                                        backgroundColor: appTheme[themeStyle].highlight,
                                                        opacity: 0.8,
                                                    },
                                                    '&.Mui-focusVisible': {
                                                        boxShadow: `0 0 0 8px ${appTheme[themeStyle].highlight}20`,
                                                    },
                                                },
                                                '& .MuiSlider-valueLabel': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                    color: appTheme[themeStyle].pure_black,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                },
                                                '& .MuiSlider-mark': {
                                                    backgroundColor: appTheme[themeStyle].form_divider,
                                                },
                                                '& .MuiSlider-markLabel': {
                                                    color: appTheme[themeStyle].grey,
                                                    fontSize: '0.75rem',
                                                },
                                            }}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={value => formatBytesReadable(value)}
                                            marks={[
                                                { value: 0, label: '0 B' },
                                                { value: 1024 * 1024 * 1024, label: '1 GB' },
                                                { value: 5 * 1024 * 1024 * 1024, label: '5 GB' },
                                                { value: 10 * 1024 * 1024 * 1024, label: '10 GB' },
                                            ]}
                                        />
                                        <Typography
                                            sx={{
                                                color: appTheme[themeStyle].grey,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                                minWidth: '80px',
                                                textAlign: 'right',
                                            }}>
                                            {formatBytesToMB(currentValue || 0)} MB
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }

                        // Render regular text input for non-slider fields
                        return (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography
                                    sx={{
                                        color: appTheme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        minWidth: 200,
                                    }}>
                                    {label}
                                </Typography>
                                <TextField
                                    value={displayValue}
                                    onChange={e => {
                                        if (isPrice) {
                                            const cents = parsePrice(e.target.value);
                                            handleToggle(path, cents);
                                        } else if (isBytes) {
                                            const bytes = parseMBToBytes(e.target.value);
                                            handleToggle(path, bytes);
                                        } else {
                                            handleNumberChange(path, e.target.value);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: appTheme[themeStyle].tag_white,
                                            '& fieldset': {
                                                borderColor: appTheme[themeStyle].form_divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: appTheme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: appTheme[themeStyle].grey,
                                        },
                                    }}
                                />
                                {isBytes && (
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].grey,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                        }}>
                                        MB
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: appTheme[themeStyle].deep_grey_lighter,
                    backgroundImage: 'none',
                    minHeight: isMobile ? '100vh' : 600,
                    maxHeight: isMobile ? '100vh' : '90vh',
                    borderRadius: isMobile ? 0 : 2,
                },
            }}>
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    color: appTheme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    borderBottom: `1px solid ${appTheme[themeStyle].form_divider}`,
                    position: 'relative',
                    pr: 6,
                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                }}>
                Global Configuration
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: appTheme[themeStyle].tag_white,
                        '&:hover': {
                            backgroundColor: appTheme[themeStyle].button_background,
                        },
                    }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack spacing={3}>
                    {/* Country Selector */}
                    <Box sx={{ my: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel
                                sx={{
                                    color: appTheme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '&.Mui-focused': {
                                        color: appTheme[themeStyle].highlight,
                                    },
                                }}>
                                Select Country
                            </InputLabel>
                            <Select
                                value={selectedCountry}
                                label="Select Country"
                                onChange={e => handleCountryChange(e.target.value)}
                                disabled={fetchingGlobalConfig || updatingConfig}
                                sx={{
                                    color: appTheme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: appTheme[themeStyle].form_divider,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: appTheme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: appTheme[themeStyle].highlight,
                                    },
                                    '& .MuiSelect-icon': {
                                        color: appTheme[themeStyle].tag_white,
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: appTheme[themeStyle].deep_grey_lighter,
                                            color: appTheme[themeStyle].tag_white,
                                            maxHeight: 300,
                                        },
                                    },
                                }}>
                                {countries.map(country => (
                                    <MenuItem
                                        key={country.code}
                                        value={country.code}
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': {
                                                backgroundColor: appTheme[themeStyle].button_background,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: appTheme[themeStyle].highlight,
                                                color: appTheme[themeStyle].pure_black,
                                                '&:hover': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                    opacity: 0.9,
                                                },
                                            },
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                                            <Typography>{country.name}</Typography>
                                            <Typography sx={{ color: appTheme[themeStyle].grey, fontSize: '0.875rem' }}>
                                                ({country.code})
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${appTheme[themeStyle].form_divider}`,
                            bgcolor: 'rgba(255,255,255,0.02)',
                        }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={applyToAllCountries}
                                    onChange={e => setApplyToAllCountries(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: appTheme[themeStyle].highlight,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: appTheme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        color: appTheme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Apply changed fields to all countries (ring0)
                                </Typography>
                            }
                        />
                        <Typography
                            sx={{
                                color: appTheme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                                mt: 1,
                                maxWidth: '720px',
                            }}>
                            When enabled, every field you change is bulk-applied across all countries in this ring.
                            Nested objects (e.g. features, buzzbridge) are set as whole objects on each country.
                        </Typography>
                        {applyToAllCountries && pendingPatchFieldLabels.length > 0 && (
                            <Typography
                                sx={{
                                    color: appTheme[themeStyle].highlight,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.8rem',
                                    mt: 1.5,
                                }}>
                                Will apply: {pendingPatchFieldLabels.join(', ')}
                            </Typography>
                        )}
                    </Box>

                    {fetchingGlobalConfig ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: appTheme[themeStyle].highlight }} />
                        </Box>
                    ) : (
                        <>
                            {renderToggleSection('Audition Filters', [
                                {
                                    key: 'rehearsal_date',
                                    label: 'Rehearsal Date',
                                    path: 'audition_filters.rehearsal_date',
                                },
                                {
                                    key: 'required_media',
                                    label: 'Required Media',
                                    path: 'audition_filters.required_media',
                                },
                                { key: 'age_range', label: 'Age Range', path: 'audition_filters.age_range' },
                                {
                                    key: 'filming_location',
                                    label: 'Filming Location',
                                    path: 'audition_filters.filming_location',
                                },
                                { key: 'compensation', label: 'Compensation', path: 'audition_filters.compensation' },
                                {
                                    key: 'audition_type',
                                    label: 'Audition Type',
                                    path: 'audition_filters.audition_type',
                                },
                                {
                                    key: 'required_skill',
                                    label: 'Required Skill',
                                    path: 'audition_filters.required_skill',
                                },
                                { key: 'tags', label: 'Tags', path: 'audition_filters.tags' },
                                {
                                    key: 'employment_type',
                                    label: 'Employment Type',
                                    path: 'audition_filters.employment_type',
                                },
                            ])}

                            {renderToggleSection('Talent Filters', [
                                { key: 'location', label: 'Location', path: 'talent_filter.location' },
                                { key: 'age_range', label: 'Age Range', path: 'talent_filter.age_range' },
                                { key: 'height', label: 'Height', path: 'talent_filter.height' },
                                { key: 'skills', label: 'Skills', path: 'talent_filter.skills' },
                                { key: 'sex', label: 'Sex', path: 'talent_filter.sex' },
                                { key: 'hair_color', label: 'Hair Color', path: 'talent_filter.hair_color' },
                                { key: 'body_type', label: 'Body Type', path: 'talent_filter.body_type' },
                                { key: 'weight', label: 'Weight', path: 'talent_filter.weight' },
                                {
                                    key: 'language_spoken',
                                    label: 'Language Spoken',
                                    path: 'talent_filter.language_spoken',
                                },
                                {
                                    key: 'special_skills',
                                    label: 'Special Skills',
                                    path: 'talent_filter.special_skills',
                                },
                            ])}

                            {renderToggleSection('Features', [
                                { key: 'voting', label: 'Voting', path: 'features.voting' },
                                { key: 'movie_ticket', label: 'Movie Ticket', path: 'features.movie_ticket' },
                                { key: 'contest_emails', label: 'Contest Emails', path: 'features.contest_emails' },
                                { key: 'audition', label: 'Audition', path: 'features.audition' },
                                { key: 'campaigns', label: 'Campaigns', path: 'features.campaigns' },
                                { key: 'buzzbridge', label: 'BuzzBridge', path: 'features.buzzbridge' },
                                { key: 'ugc_only', label: 'UGC Only', path: 'features.ugc_only' },
                                { key: 'discover', label: 'Discover', path: 'features.discover' },
                                { key: 'events', label: 'Events', path: 'features.events' },
                                { key: 'talents', label: 'Talents', path: 'features.talents' },
                                { key: 'feed', label: 'Feed', path: 'features.feed' },
                                { key: 'ads', label: 'Ads', path: 'features.ads' },
                                { key: 'hls_play', label: 'HLS Play', path: 'features.hls_play' },
                                { key: 'acts', label: 'Acts', path: 'features.acts' },
                                { key: 'social_manager', label: 'Social Manager', path: 'features.social_manager' },
                                {
                                    key: 'influencer_platform_avatar_search',
                                    label: 'Influencer Platform Avatar Search',
                                    path: 'features.influencer_platform_avatar_search',
                                },
                                {
                                    key: 'creator_subscription_trial',
                                    label: 'Creator Subscription Trial',
                                    path: 'features.creator_subscription_trial',
                                },
                            ])}

                            {renderToggleSection('Account Features', [
                                { key: 'movies', label: 'Movies', path: 'features.account_features.movies' },
                                {
                                    key: 'productions',
                                    label: 'Productions',
                                    path: 'features.account_features.productions',
                                },
                                {
                                    key: 'contributions',
                                    label: 'Contributions',
                                    path: 'features.account_features.contributions',
                                },
                                { key: 'profile', label: 'Profile', path: 'features.account_features.profile' },
                                { key: 'reviews', label: 'Reviews', path: 'features.account_features.reviews' },
                                {
                                    key: 'applications',
                                    label: 'Applications',
                                    path: 'features.account_features.applications',
                                },
                                { key: 'settings', label: 'Settings', path: 'features.account_features.settings' },
                                { key: 'people', label: 'People', path: 'features.account_features.people' },
                                { key: 'tickets', label: 'Tickets', path: 'features.account_features.tickets' },
                                { key: 'likes', label: 'Likes', path: 'features.account_features.likes' },
                                {
                                    key: 'boxofix_score',
                                    label: 'Boxofix Score',
                                    path: 'features.account_features.boxofix_score',
                                },
                                {
                                    key: 'subscriptions',
                                    label: 'Subscriptions',
                                    path: 'features.account_features.subscriptions',
                                },
                            ])}

                            {renderNumberSection('Limits', [
                                {
                                    key: 'default_casting_limit',
                                    label: 'Default Casting Limit',
                                    path: 'default_casting_limit',
                                },
                                {
                                    key: 'default_application_limit',
                                    label: 'Default Application Limit',
                                    path: 'default_application_limit',
                                },
                                { key: 'act_limit', label: 'Act Limit', path: 'act_limit' },
                                {
                                    key: 'contest_submission_limit',
                                    label: 'Contest Submission Limit',
                                    path: 'contest_submission_limit',
                                },
                            ])}

                            <Accordion
                                defaultExpanded
                                sx={{
                                    backgroundColor: 'transparent',
                                    color: appTheme[themeStyle].tag_white,
                                    '& .MuiAccordionSummary-root': {
                                        color: appTheme[themeStyle].tag_white,
                                    },
                                    '& .MuiAccordionDetails-root': {
                                        color: appTheme[themeStyle].tag_white,
                                    },
                                }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                        Payout &amp; Withdrawal
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={3}>
                                        <GlobalConfigurationDollarCentsField
                                            label="Minimum Withdrawal"
                                            cents={
                                                config.minimum_withdrawal_cents ??
                                                baselineConfig.minimum_withdrawal_cents
                                            }
                                            onChangeCents={cents => handleToggle('minimum_withdrawal_cents', cents)}
                                            themeStyle={themeStyle}
                                            placeholder="60.00"
                                            description="Platform minimum payout amount in USD. Stored in cents (e.g. $60.00 = 6000). Wallet and cash-out UIs read this from the balance API at runtime."
                                        />
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            {renderNumberSection('Ticket Pricing (in dollars)', [
                                {
                                    key: 'min_ticket_price',
                                    label: 'Minimum Ticket Price',
                                    path: 'min_ticket_price',
                                    isPrice: true,
                                },
                                {
                                    key: 'ticket_flat_fee',
                                    label: 'Ticket Flat Fee',
                                    path: 'ticket_flat_fee',
                                    isPrice: true,
                                },
                                {
                                    key: 'ticket_additional_fee',
                                    label: 'Ticket Additional Fee (%)',
                                    path: 'ticket_additional_fee',
                                },
                                {
                                    key: 'max_ticket_price',
                                    label: 'Maximum Ticket Price',
                                    path: 'max_ticket_price',
                                    isPrice: true,
                                },
                            ])}

                            {renderNumberSection('Submission Pricing (in dollars)', [
                                {
                                    key: 'min_submission_price',
                                    label: 'Minimum Submission Price',
                                    path: 'min_submission_price',
                                    isPrice: true,
                                },
                                {
                                    key: 'submission_flat_fee',
                                    label: 'Submission Flat Fee',
                                    path: 'submission_flat_fee',
                                    isPrice: true,
                                },
                                {
                                    key: 'added_percentage_per_submission',
                                    label: 'Added Percentage per Submission (%)',
                                    path: 'added_percentage_per_submission',
                                },
                                {
                                    key: 'max_submission_price',
                                    label: 'Maximum Submission Price',
                                    path: 'max_submission_price',
                                    isPrice: true,
                                },
                            ])}

                            {renderNumberSection('Campaign Pricing (in dollars)', [
                                {
                                    key: 'campaign_max',
                                    label: 'Campaign Maximum Amount',
                                    path: 'campaign_max',
                                    isPrice: true,
                                },
                                {
                                    key: 'campaign_percentage',
                                    label: 'Campaign Percentage (%)',
                                    path: 'campaign_percentage',
                                },
                                {
                                    key: 'campaign_flat_fee',
                                    label: 'Campaign Flat Fee',
                                    path: 'campaign_flat_fee',
                                    isPrice: true,
                                },
                            ])}

                            <GlobalConfigurationCampaignHostLtdEditor open={open} themeStyle={themeStyle} />

                            {renderToggleSection('BuzzBridge Auto-Link (influencer signup)', [
                                { key: 'instagram', label: 'Instagram', path: 'buzzbridge.enableAutoLink.Instagram' },
                                { key: 'youtube', label: 'YouTube', path: 'buzzbridge.enableAutoLink.YouTube' },
                                { key: 'tiktok', label: 'TikTok', path: 'buzzbridge.enableAutoLink.TikTok' },
                            ])}

                            {renderToggleSection('BuzzBridge Instagram automation', [
                                {
                                    key: 'instagram_comment_automation_enabled',
                                    label: 'Instagram comment automation',
                                    path: 'buzzbridge.instagram_comment_automation_enabled',
                                },
                                {
                                    key: 'instagram_login_enabled',
                                    label: 'Instagram Login (Connect with Instagram)',
                                    path: 'buzzbridge.instagram_login_enabled',
                                },
                            ])}

                            <GlobalConfigurationUgcEditor
                                config={config}
                                setConfig={setConfig}
                                setHasChanges={setHasChanges}
                                themeStyle={themeStyle}
                            />

                            <GlobalConfigurationBuzzbridgeLoyaltyEditor
                                config={config}
                                setConfig={setConfig}
                                setHasChanges={setHasChanges}
                                themeStyle={themeStyle}
                            />

                            <GlobalConfigurationContentScrubEditor
                                config={config}
                                setConfig={setConfig}
                                setHasChanges={setHasChanges}
                                themeStyle={themeStyle}
                            />

                            {renderToggleSection('System Settings', [
                                { key: 'use_cache', label: 'Use Cache', path: 'use_cache' },
                            ])}

                            {renderToggleSection('Two-Factor Authentication', [
                                { key: 'audme', label: 'Audme', path: 'two_fa_configuration.audme' },
                                { key: 'email', label: 'Email', path: 'two_fa_configuration.email' },
                                { key: 'sms', label: 'SMS', path: 'two_fa_configuration.sms' },
                                { key: 'whatsapp', label: 'WhatsApp', path: 'two_fa_configuration.whatsapp' },
                            ])}

                            {renderNumberSection('Cache Settings', [
                                { key: 'cache_duration', label: 'Cache Duration (ms)', path: 'cache_duration' },
                            ])}

                            {renderNumberSection('Media Control', [
                                {
                                    key: 'file_size_limit',
                                    label: 'File Size Limit',
                                    path: 'media_control.file_size_limit',
                                    isSlider: true,
                                },
                                {
                                    key: 'mobile_size_limit',
                                    label: 'Mobile Size Limit',
                                    path: 'media_control.mobile_size_limit',
                                    isSlider: true,
                                },
                                {
                                    key: 'image_size_limit',
                                    label: 'Image Size Limit',
                                    path: 'media_control.image_size_limit',
                                    isSlider: true,
                                },
                                {
                                    key: 'pagination_count',
                                    label: 'Pagination Count',
                                    path: 'media_control.pagination_count',
                                },
                            ])}

                            {renderNumberSectionWithDescription('Ad Control', [
                                {
                                    key: 'skipped',
                                    label: 'Videos after Skipped Ad',
                                    path: 'ad_control.skipped',
                                    description:
                                        'Number of videos to play before showing another ad if the user skipped the last ad',
                                },
                                {
                                    key: 'ended',
                                    label: 'Videos after Completed Ad',
                                    path: 'ad_control.ended',
                                    description:
                                        'Number of videos to play before showing another ad if the user completed the last ad',
                                },
                                {
                                    key: 'start',
                                    label: 'Videos after Started Ad',
                                    path: 'ad_control.start',
                                    description:
                                        'Number of videos to play before showing another ad if the user only started the last ad',
                                },
                            ])}

                            {renderStringSection('Playlist Settings', [
                                {
                                    key: 'past_winner_playlist',
                                    label: 'Past Winner Playlist',
                                    path: 'past_winner_playlist',
                                    placeholder: 'PL-xxxxxxxxxxxxx',
                                    description:
                                        'Please ensure the playlist exists and is public. The playlist ID must start with "PL-".',
                                    validation: (value: string) => {
                                        if (!value) return true; // Allow empty values
                                        return value.startsWith('PL-');
                                    },
                                    errorMessage: 'Playlist ID must start with "PL-"',
                                },
                            ])}

                            {renderStringSection('Social Integrations', [
                                {
                                    key: 'facebook_apps_settings_url',
                                    label: 'Facebook Apps Settings URL',
                                    path: 'facebook_apps_settings_url',
                                    placeholder: 'https://developers.facebook.com/apps/...',
                                    description:
                                        'URL to the Facebook App Settings page. Used to direct users to manage their Facebook app permissions.',
                                },
                            ])}
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: `1px solid ${appTheme[themeStyle].form_divider}` }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: appTheme[themeStyle].grey,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    variant="contained"
                    sx={{
                        backgroundColor: appTheme[themeStyle].highlight,
                        color: appTheme[themeStyle].pure_black,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover': {
                            backgroundColor: appTheme[themeStyle].highlight,
                            opacity: 0.9,
                        },
                        '&:disabled': {
                            backgroundColor: appTheme[themeStyle].grey,
                            color: appTheme[themeStyle].deep_grey,
                        },
                    }}>
                    {updatingConfig || fetchingGlobalConfig || isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GlobalConfigurationModal;
